import { config } from '../config';
import unifiedAuth from '../utils/unifiedAuth';
import type {
  Series,
  Season,
  Episode,
  SeriesListResponse,
  SeriesNavigation
} from '../types/series';

// API request helper with authentication.
// Token attachment + 401 refresh-retry is delegated to unifiedAuth.fetch()
// (the single shared implementation) instead of reading the token locally.
const apiRequest = async (endpoint: string, options: RequestInit = {}) => {
  const defaultOptions: RequestInit = {
    headers: {
      'Content-Type': 'application/json',
      ...options.headers,
    },
    ...options,
  };

  const response = await unifiedAuth.fetch(`${config.backendUrl}/api${endpoint}`, defaultOptions);

  if (!response.ok) {
    throw new Error(`API request failed: ${response.status} ${response.statusText}`);
  }

  return response.json();
};

export interface SeriesFilter {
  page?: number;
  page_size?: number;
  seriesType?: 'CINEMA_SERIES' | 'CONTENT_SERIES' | 'PODCAST_SERIES' | 'ANIMATION_SERIES';
  category?: string;
  isFeatured?: boolean;
  isCompleted?: boolean;
  search?: string;
  status?: 'DRAFT' | 'PUBLISHED' | 'ARCHIVED' | 'COMPLETED';
}

class SeriesService {
  // ===== SERIES METHODS =====

  async getAllSeries(filters: SeriesFilter = {}): Promise<SeriesListResponse> {
    const params = new URLSearchParams();
    let baseUrl = '/series';
    
    // If filtering by seriesType, use the type-specific endpoint
    if (filters.seriesType) {
      baseUrl = `/series/type/${filters.seriesType}`;
      console.log('Using type-specific endpoint for seriesType:', filters.seriesType);
    }
    
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '' && key !== 'seriesType') {
        // Map frontend parameter names to backend parameter names
        const backendKey = key === 'page_size' ? 'size' : key;
        params.append(backendKey, value.toString());
      }
    });

    const queryString = params.toString();
    const url = `${baseUrl}${queryString ? `?${queryString}` : ''}`;
    console.log('API Request URL:', url); // Debug logging
    console.log('Filters sent:', filters); // Debug logging
    return apiRequest(url);
  }

  async getSeriesById(id: string): Promise<Series> {
    if (!id || id === 'undefined') {
      throw new Error(`Invalid series ID: ${id}`);
    }
    
    try {
      const response = await apiRequest(`/series/${id}`);
      // The API returns {success: true, data: {...}}, so extract the data
      const seriesData = response.data || response;
      
      if (!seriesData || !seriesData.id) {
        throw new Error(`Series with ID ${id} not found or invalid`);
      }
      
      return seriesData;
    } catch (error) {
      if (error instanceof Error) {
        // Provide more specific error messages
        if (error.message.includes('404') || error.message.includes('not found')) {
          throw new Error(`Series with ID ${id} does not exist`);
        } else if (error.message.includes('500')) {
          throw new Error(`Server error while loading series ${id}. Please try again later.`);
        }
      }
      
      // Re-throw the original error if it's not a known case
      throw error;
    }
  }

  async getSeriesByType(seriesType: string, filters: SeriesFilter = {}): Promise<SeriesListResponse> {
    const params = new URLSearchParams();
    
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        params.append(key, value.toString());
      }
    });

    const queryString = params.toString();
    return apiRequest(`/series/type/${seriesType}${queryString ? `?${queryString}` : ''}`);
  }

  // Content-type specific series methods
  async getCinemaSeries(filters: SeriesFilter = {}): Promise<SeriesListResponse> {
    const params = new URLSearchParams();
    
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        params.append(key, value.toString());
      }
    });

    const queryString = params.toString();
    return apiRequest(`/series/cinema${queryString ? `?${queryString}` : ''}`);
  }

  async getContentSeries(filters: SeriesFilter = {}): Promise<SeriesListResponse> {
    const params = new URLSearchParams();
    
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        params.append(key, value.toString());
      }
    });

    const queryString = params.toString();
    return apiRequest(`/series/content${queryString ? `?${queryString}` : ''}`);
  }

  async getPodcastSeries(filters: SeriesFilter = {}): Promise<SeriesListResponse> {
    const params = new URLSearchParams();
    
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        params.append(key, value.toString());
      }
    });

    const queryString = params.toString();
    return apiRequest(`/series/podcasts${queryString ? `?${queryString}` : ''}`);
  }

  async getAnimationSeries(filters: SeriesFilter = {}): Promise<SeriesListResponse> {
    const params = new URLSearchParams();
    
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        params.append(key, value.toString());
      }
    });

    const queryString = params.toString();
    return apiRequest(`/series/animations${queryString ? `?${queryString}` : ''}`);
  }

  async getFeaturedSeries(filters: SeriesFilter = {}): Promise<SeriesListResponse> {
    const params = new URLSearchParams();
    
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        params.append(key, value.toString());
      }
    });

    const queryString = params.toString();
    return apiRequest(`/series/featured${queryString ? `?${queryString}` : ''}`);
  }

  // ===== SEASON METHODS =====

  async getSeasonsBySeries(seriesId: string): Promise<Season[]> {
    if (!seriesId || seriesId === 'undefined') {
      throw new Error(`Invalid series ID: ${seriesId}`);
    }
    
    const response = await apiRequest(`/series/${seriesId}/seasons`);
    return response.data || response;
  }

  async getSeasonByNumber(seriesId: string, seasonNumber: number): Promise<Season> {
    const response = await apiRequest(`/series/${seriesId}/seasons/${seasonNumber}`);
    return response.data || response;
  }

  // ===== EPISODE METHODS =====

  async getEpisodesBySeasonNumber(seriesId: string, seasonNumber: number): Promise<Episode[]> {
    const response = await apiRequest(`/series/${seriesId}/seasons/${seasonNumber}/episodes`);
    return response.data || response;
  }

  async getEpisodeByNumber(seriesId: string, seasonNumber: number, episodeNumber: number): Promise<Episode> {
    const response = await apiRequest(`/series/${seriesId}/seasons/${seasonNumber}/episodes/${episodeNumber}`);
    return response.data || response;
  }

  async getAllEpisodesBySeries(seriesId: string): Promise<Episode[]> {
    if (!seriesId || seriesId === 'undefined') {
      throw new Error(`Invalid series ID: ${seriesId}`);
    }
    
    try {
      const response = await apiRequest(`/series/${seriesId}/episodes`);
      const episodesData = response.data || response;
      
      if (!Array.isArray(episodesData)) {
        console.warn(`Expected episodes array for series ${seriesId}, got:`, episodesData);
        return [];
      }
      
      // Validate episode structure
      const validEpisodes = episodesData.filter(episode => {
        if (!episode || !episode.id) {
          console.warn(`Invalid episode found in series ${seriesId}:`, episode);
          return false;
        }
        return true;
      });
      
      // Loaded episodes for series - debug removed for production
      return validEpisodes;
    } catch (error) {
      if (error instanceof Error) {
        if (error.message.includes('404') || error.message.includes('not found')) {
          console.warn(`No episodes found for series ${seriesId}`);
          return [];
        } else if (error.message.includes('500')) {
          throw new Error(`Server error while loading episodes for series ${seriesId}. Please try again later.`);
        }
      }
      
      console.error(`Error loading episodes for series ${seriesId}:`, error);
      throw error;
    }
  }

  // ===== NAVIGATION METHODS =====

  async getNextEpisode(episodeId: string): Promise<Episode | null> {
    const response = await apiRequest(`/series/episodes/${episodeId}/next`);
    return response.data || null;
  }

  async getPreviousEpisode(episodeId: string): Promise<Episode | null> {
    const response = await apiRequest(`/series/episodes/${episodeId}/previous`);
    return response.data || null;
  }

  async getSeriesNavigation(episodeId: string): Promise<SeriesNavigation | null> {
    try {
      const [_nextEpisode, _previousEpisode] = await Promise.all([
        this.getNextEpisode(episodeId),
        this.getPreviousEpisode(episodeId)
      ]);

      // We would need the current episode details to build complete navigation
      // This is a simplified version - in practice, you'd get the episode details first
      return null; // Placeholder - implement based on your needs
    } catch (error) {
      console.error('Error getting series navigation:', error);
      return null;
    }
  }

  // ===== CONTENT INTEGRATION =====

  async getContentSeriesInfo(contentId: string): Promise<{
    series?: Series;
    season?: Season;
    episode?: Episode;
    seasonNumber?: number;
    episodeNumber?: number;
  } | null> {
    try {
      const response = await apiRequest(`/content/${contentId}/series-info`);
      return response.data || null;
    } catch (error) {
      console.error('Error getting content series info:', error);
      return null;
    }
  }

  // ===== UTILITY METHODS =====

  formatDuration(seconds: number): string {
    if (seconds < 60) {
      return `${seconds}s`;
    } else if (seconds < 3600) {
      const minutes = Math.floor(seconds / 60);
      const remainingSeconds = seconds % 60;
      return remainingSeconds > 0 ? `${minutes}m ${remainingSeconds}s` : `${minutes}m`;
    } else {
      const hours = Math.floor(seconds / 3600);
      const minutes = Math.floor((seconds % 3600) / 60);
      return minutes > 0 ? `${hours}h ${minutes}m` : `${hours}h`;
    }
  }

  formatEpisodeTitle(series: Series, season: Season, episode: Episode): string {
    return `${series.title} - S${season.seasonNumber}E${episode.episodeNumber}: ${episode.title}`;
  }

  getSeriesTypeLabel(seriesType: string): string {
    switch (seriesType) {
      case 'CINEMA_SERIES': return 'Cinema Series';
      case 'CONTENT_SERIES': return 'Content Series';
      case 'PODCAST_SERIES': return 'Podcast Series';
      case 'ANIMATION_SERIES': return 'Animation Series';
      default: return 'Series';
    }
  }

  // Transform backend episode to include computed fields
  transformEpisode(episode: Episode): Episode {
    return {
      ...episode,
      duration: Math.floor(episode.durationSeconds / 60), // Convert to minutes
      durationFormatted: this.formatDuration(episode.durationSeconds),
    };
  }

  // Transform backend episodes array
  transformEpisodes(episodes: Episode[]): Episode[] {
    return episodes.map(episode => this.transformEpisode(episode));
  }

  // ===== ADMIN MANAGEMENT METHODS =====

  async createSeries(seriesData: {
    title: string;
    description?: string;
    seriesType: 'CINEMA_SERIES' | 'CONTENT_SERIES' | 'PODCAST_SERIES' | 'ANIMATION_SERIES';
    category?: string;
    tags?: string;
    isFeatured?: boolean;
    isPremium?: boolean;
    status?: 'DRAFT' | 'PUBLISHED' | 'ARCHIVED' | 'COMPLETED';
  }): Promise<Series> {
    const response = await apiRequest('/series', {
      method: 'POST',
      body: JSON.stringify(seriesData),
    });
    return response.data || response;
  }

  async updateSeries(id: string, seriesData: {
    title?: string;
    description?: string;
    seriesType?: 'CINEMA_SERIES' | 'CONTENT_SERIES' | 'PODCAST_SERIES' | 'ANIMATION_SERIES';
    category?: string;
    tags?: string;
    isFeatured?: boolean;
    isPremium?: boolean;
    status?: 'DRAFT' | 'PUBLISHED' | 'ARCHIVED' | 'COMPLETED';
  }): Promise<Series> {
    const response = await apiRequest(`/series/${id}`, {
      method: 'PUT',
      body: JSON.stringify(seriesData),
    });
    return response.data || response;
  }

  async deleteSeries(id: string): Promise<void> {
    await apiRequest(`/series/${id}`, {
      method: 'DELETE',
    });
  }

  async createSeason(seriesId: string, seasonData: {
    title: string;
    description?: string;
    seasonNumber: number;
    releaseDate?: string;
    status?: 'DRAFT' | 'PUBLISHED' | 'ARCHIVED' | 'COMPLETED';
  }): Promise<Season> {
    const response = await apiRequest(`/series/${seriesId}/seasons`, {
      method: 'POST',
      body: JSON.stringify(seasonData),
    });
    return response.data || response;
  }

  async updateSeason(seriesId: string, seasonId: string, seasonData: {
    title?: string;
    description?: string;
    seasonNumber?: number;
    releaseDate?: string;
    status?: 'DRAFT' | 'PUBLISHED' | 'ARCHIVED' | 'COMPLETED';
  }): Promise<Season> {
    const response = await apiRequest(`/series/${seriesId}/seasons/${seasonId}`, {
      method: 'PUT',
      body: JSON.stringify(seasonData),
    });
    return response.data || response;
  }

  async deleteSeason(seriesId: string, seasonId: string): Promise<void> {
    await apiRequest(`/series/${seriesId}/seasons/${seasonId}`, {
      method: 'DELETE',
    });
  }

  async createEpisode(seriesId: string, seasonId: string, episodeData: {
    title: string;
    description?: string;
    episodeNumber: number;
    contentId: string;
    airDate?: string;
    status?: 'DRAFT' | 'PUBLISHED' | 'ARCHIVED' | 'SCHEDULED';
  }): Promise<Episode> {
    const response = await apiRequest(`/series/${seriesId}/seasons/${seasonId}/episodes`, {
      method: 'POST',
      body: JSON.stringify(episodeData),
    });
    return response.data || response;
  }

  async updateEpisode(seriesId: string, seasonId: string, episodeId: string, episodeData: {
    title?: string;
    description?: string;
    episodeNumber?: number;
    contentId?: string;
    airDate?: string;
    status?: 'DRAFT' | 'PUBLISHED' | 'ARCHIVED' | 'SCHEDULED';
  }): Promise<Episode> {
    const response = await apiRequest(`/series/${seriesId}/seasons/${seasonId}/episodes/${episodeId}`, {
      method: 'PUT',
      body: JSON.stringify(episodeData),
    });
    return response.data || response;
  }

  async deleteEpisode(seriesId: string, seasonId: string, episodeId: string): Promise<void> {
    await apiRequest(`/series/${seriesId}/seasons/${seasonId}/episodes/${episodeId}`, {
      method: 'DELETE',
    });
  }
}

// Create and export singleton instance
const seriesService = new SeriesService();
export default seriesService;